import { logoutAPI } from '@Src/apiServices/authService';
import fyno from '@fyno/websdk';
import jwt_decode from 'jwt-decode';
import { getAppSource, getJwtKeySourceKey } from '.';

const jwtTokenEnabledKey = 'jwt_token_enabled';
const jwtTokenKey = 'jwt_token';
const remindLater = 'remind_later';
const accessibleUIResource = 'accessible_ui_resource';
const msiteData = 'msite_data';

const AUTHORIZED_ROLES = 'authorized_roles';
const BUSINESS_UNIT = 'business_unit';
const COMPANY_ID = 'company_id';
const COMPANY_NAME = 'company_name';
const EMAIL = 'email';
const FIRST_NAME = 'first_name';
const LAST_NAME = 'last_name';
const PHONE_NUMBER = 'phone_number';
const ROLES = 'roles';
const SERVICES = 'services';
const CENTER_KEYS = 'user_centers';
const CENTER_KEY = 'user_center';
const USER_ID = 'user_id';
const IS_AB = 'is_ab';
const AUD = 'aud';
const USER_TYPE = 'user_type';

export interface TypeUserData {
  [AUTHORIZED_ROLES]?: string[];
  [BUSINESS_UNIT]?: string[];
  [COMPANY_ID]?: string;
  [COMPANY_NAME]?: string;
  [EMAIL]?: string;
  [FIRST_NAME]?: string;
  [LAST_NAME]?: string;
  [PHONE_NUMBER]?: string;
  [ROLES]?: string[];
  [SERVICES]?: string[];
  [CENTER_KEY]?: string[];
  [CENTER_KEYS]?: string;
  [USER_ID]?: string;
  [accessibleUIResource]?: string;
  [IS_AB]?: boolean;
  [AUD]?: string;
  [USER_TYPE]?: string;
}

let userData: TypeUserData = {};

export const decodeJwtToken = (token = ''): unknown => {
  const decoded = jwt_decode(token);
  return decoded;
};

export const setAccessibleUIResources = (uiResources: string): void => {
  localStorage.setItem(accessibleUIResource, uiResources);
  userData = { ...userData, [accessibleUIResource]: uiResources };
};

const setLocalStorage = <T>(key: string, value: T): void => {
  localStorage.setItem(key, String(value));
};
const removeLocalStorage = (key: string): void => {
  localStorage.removeItem(key);
};

export const setJwtToken = (jwtToken: string): void => {
  setLocalStorage(jwtTokenKey, jwtToken);
  setLocalStorage(jwtTokenEnabledKey, true);
  const domain = window.location.hostname;
  const subdomain = domain.substr(domain.indexOf('.') + 1);
  // document.cookie = `${jwtTokenKey}=${jwtToken};Domain=${domain};Path=/;Max-Age=2592000;secure=true;samesite=None;`;
  // document.cookie = `source=${getAppSource()};Domain=${domain};Path=/;Max-Age=2592000;secure=true;samesite=None;`;

  setSecureCookie(jwtTokenKey, jwtToken, subdomain);
  setSecureCookie(getJwtKeySourceKey(), jwtToken, domain);
  setSecureCookie('source', getAppSource(), subdomain);
};

export const setUserDetails = (UserResponse: TypeUserData): void => {
  userData = UserResponse;
  setLocalStorage(AUTHORIZED_ROLES, UserResponse[AUTHORIZED_ROLES]);
  setLocalStorage(BUSINESS_UNIT, UserResponse[BUSINESS_UNIT]);
  setLocalStorage(COMPANY_ID, UserResponse[COMPANY_ID]);
  setLocalStorage(COMPANY_NAME, UserResponse[COMPANY_NAME]);
  setLocalStorage(EMAIL, UserResponse[EMAIL]);
  setLocalStorage(FIRST_NAME, UserResponse[FIRST_NAME]);
  setLocalStorage(LAST_NAME, UserResponse[LAST_NAME]);
  setLocalStorage(PHONE_NUMBER, UserResponse[PHONE_NUMBER]);
  setLocalStorage(ROLES, UserResponse[ROLES]);
  setLocalStorage(SERVICES, UserResponse[SERVICES]);
  setLocalStorage(CENTER_KEY, UserResponse[CENTER_KEY]);
  setLocalStorage(CENTER_KEYS, UserResponse[CENTER_KEYS]);
  setLocalStorage(USER_ID, UserResponse[USER_ID]);
  setLocalStorage(IS_AB, UserResponse[IS_AB] || false);
  setLocalStorage(AUD, UserResponse[AUD] || '');
  setLocalStorage(USER_TYPE, UserResponse[USER_TYPE] || '');
};

export const setMsiteData = (data: any): void => {
  setLocalStorage(msiteData, JSON.stringify(data));
};

export const removeUserDetails = (): void => {
  removeLocalStorage(AUTHORIZED_ROLES);
  removeLocalStorage(BUSINESS_UNIT);
  removeLocalStorage(COMPANY_ID);
  removeLocalStorage(COMPANY_NAME);
  removeLocalStorage(EMAIL);
  removeLocalStorage(FIRST_NAME);
  removeLocalStorage(LAST_NAME);
  removeLocalStorage(PHONE_NUMBER);
  removeLocalStorage(ROLES);
  removeLocalStorage(SERVICES);
  removeLocalStorage(CENTER_KEY);
  removeLocalStorage(CENTER_KEYS);
  removeLocalStorage(USER_ID);
  removeLocalStorage(AUD);
  removeLocalStorage(USER_TYPE);

  removeLocalStorage(accessibleUIResource);
};

export const getAuthorizedRoles = (): string[] | undefined =>
  userData[AUTHORIZED_ROLES];

export const getBusinessUnit = (): string[] | undefined =>
  userData[BUSINESS_UNIT];
export const getCompanyId = (): string | undefined => userData[COMPANY_ID];
export const getCompanyName = (): string | undefined => userData[COMPANY_NAME];
export const getEmail = (): string | undefined => userData[EMAIL];
export const getFirstName = (): string | undefined => userData[FIRST_NAME];
export const getLastName = (): string | undefined => userData[LAST_NAME];
export const getPhoneNumber = (): string | undefined => userData[PHONE_NUMBER];
export const getRoles = (): string[] | undefined => userData[ROLES];
export const getServices = (): string[] | undefined => userData[SERVICES];
export const getUserCenter = (): string[] | undefined => userData[CENTER_KEY];
export const getUserCenters = (): string | undefined => userData[CENTER_KEYS];
export const getUserId = (): string | undefined =>
  userData[USER_ID] || getLocalStorageData(USER_ID) || '';
export const getAudSource = (): string | undefined =>
  userData[AUD] || getLocalStorageData(AUD) || '';
export const getUserType = (): string | undefined =>
  userData[USER_TYPE] || getLocalStorageData(USER_TYPE) || '';

export const getLocalStorageData = <T>(itemKey: string): T | null =>
  localStorage.getItem(itemKey) as T | null;

export const getJwtToken = (): string =>
  getLocalStorageData<string>(jwtTokenKey) || '';

export const getJwtTokenEnable = (): boolean =>
  getLocalStorageData<boolean>(jwtTokenEnabledKey) || false;

export const getUserData = (): TypeUserData => userData;

export const getMsiteData = (): string => {
  const data = getLocalStorageData<string>(msiteData);
  return data ? JSON.parse(data) : '';
};
export const removeMsiteData = (): void => {
  localStorage.removeItem(msiteData);
};

export const isLoggedIn = (): boolean => {
  const authToken = getJwtToken();
  return !!authToken;
};

export const removeJwtToken = (): void => {
  localStorage.removeItem(jwtTokenKey);
  localStorage.removeItem(jwtTokenEnabledKey);
};

export const logoutFunc = () => {
  localStorage.clear();
  const domain = window.location.hostname;
  const subdomain = domain.substr(domain.indexOf('.') + 1);
  setSecureCookie(getJwtKeySourceKey(), '', domain, -99999999);

  window.location.href = '/';
};
export const logOut = async (): void => {
  // const userId = getUserId();
  // await logoutAPI(userId);
  try {
    await fyno.reset();
  } finally {
    logoutFunc();
  }
};

// adding to get zeroth index
export const getCenterIdForZerothIndex = (): string => {
  const centers = getUserCenter();
  if (typeof centers === String) return centers;
  if (centers?.length) return centers[0];
  return '';
};

export const isInternalPlatformURL = () =>
  window.location.host.includes('internal');
export const isInternalPlatform = (): boolean => {
  if (isLoggedIn()) {
    return getAudSource() === 'InternalApp' && getUserType() === 'internal';
  }
  return isInternalPlatformURL();
};

export const isNextPlatform = (): boolean => {
  if (isLoggedIn()) {
    return getAudSource() === 'BizongoApp' && getUserType() === 'external';
  }
  return window.location.host.includes('next');
};

export const internalRoles = () => localStorage.getItem('roles');

export const getCookie = (cookieName: string): string | null => {
  const cookies = document.cookie.split(';');

  for (let cookie of cookies) {
    cookie = cookie.trim();

    // Check if this cookie starts with the cookieName=
    if (cookie.startsWith(`${cookieName}=`)) {
      return cookie.substring(cookieName.length + 1);
    }
  }

  return null; // Cookie not found
};

const setSecureCookie = (
  key: string,
  value: string,
  domain: string,
  maxAge = 604800
) => {
  document.cookie = `${key}=${value}; Domain=${domain}; Path=/; Max-Age=${maxAge}; Secure; SameSite=None`;
};
